import React from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Grid, Card, Heading, Text, Flex, Box } from "@radix-ui/themes"
import { CalendarDays, AlertTriangle, TrendingUp, ChevronRight } from "lucide-react"
import { useLanguage } from "../../contexts/LanguageContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"

const RestaurantReportsPage: React.FC = () => {
  const { restaurantId } = useParams()
  const navigate = useNavigate()
  const { t, dir } = useLanguage()

  const reports = [
    {
      title: "Daily Sales Summary",
      description: "End of day revenue and exact payment method reconciliation (Cash vs Card vs Mobile).",
      icon: <CalendarDays className="w-6 h-6 text-blue-600" />,
      path: `/restaurant/${restaurantId}/reports/daily-sales`,
      gradient: "from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20",
    },
    {
      title: "Voids & Comps Exception Log",
      description: "Security record of cashier overrides, cancelled tickets, and comped items.",
      icon: <AlertTriangle className="w-6 h-6 text-red-600" />,
      path: `/restaurant/${restaurantId}/reports/voids-comps`,
      gradient: "from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20",
    },
    {
      title: "Menu Item Popularity",
      description: "Itemized sales performance identifying Star performers and Dog underperformers.",
      icon: <TrendingUp className="w-6 h-6 text-green-600" />,
      path: `/restaurant/${restaurantId}/reports/menu-popularity`,
      gradient: "from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20",
    }
  ]

  return (
    <div className="space-y-6" dir={dir}>
      <PageHeader
        title="Printable Reports"
        subtitle="Generate formatted, print-ready A4 PDF reports for accounting and auditing."
      />

      <Grid columns={{ initial: "1", md: "2", lg: "3" }} gap="6" className="mt-8">
        {reports.map((report, index) => (
          <Card
            key={index}
            className="cursor-pointer hover:shadow-md transition-all duration-200 border border-gray-200 dark:border-gray-800"
            onClick={() => navigate(report.path)}
          >
            <Flex direction="column" className="h-full">
              <Box className={`p-4 rounded-t-lg bg-gradient-to-br ${report.gradient} border-b border-gray-100 dark:border-gray-800`}>
                <Flex justify="between" align="start">
                  <Box className="p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                    {report.icon}
                  </Box>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Flex>
                <Heading size="4" mt="4" className="text-gray-900 dark:text-gray-100">
                  {report.title}
                </Heading>
              </Box>
              <Box className="p-4 flex-1">
                <Text size="2" color="gray" className="leading-relaxed">
                  {report.description}
                </Text>
              </Box>
            </Flex>
          </Card>
        ))}
      </Grid>
    </div>
  )
}

export default RestaurantReportsPage 